import React, { useState, useRef } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  ScrollView,
} from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { QuickReplySetForm, QuickReplySetFormData } from '../components/quick-replies';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const QuickReplySetCreateScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [loading, setLoading] = useState(false);
  const formSubmitRef = useRef<(() => void) | null>(null);

  const handleSubmit = async (formData: QuickReplySetFormData) => {
    try {
      setLoading(true);

      const response = await apiService.createQuickReplySet({
        name: formData.name,
        description: formData.description,
        isActive: formData.isActive,
        isDefault: formData.isDefault,
        items: [], // Items will be added in Part 2
      });

      if (response.statusCode === 1000 && response.data) {
        showNotification('success', 'Success', 'Quick reply set created successfully.');
        setTimeout(() => {
          navigateTo('quick-replies');
        }, 1500);
      } else {
        const errorMessage =
          response.errors?.[0]?.message || 'Failed to create quick reply set';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error creating quick reply set:', error);
      const errorMessage = error.message || 
                          error.errors?.[0]?.message || 
                          'Failed to create quick reply set';
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('quick-replies');
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent="empty"
      formHeader={
        <FormHeader
          title="Reply Set"
          subtitle="Set up a new quick reply set for your channels"
          onBack={handleCancel}
          actions={[
            {
              icon: 'checkmark',
              onPress: () => formSubmitRef.current?.(),
              primary: true,
              loading,
              disabled: loading,
            },
          ]}
        />
      }
    >
      <View style={styles.wrapper}>
        <ScrollView
          style={styles.container}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.formContainer}>
            <QuickReplySetForm
              onSubmit={handleSubmit}
              submitLabel="Create Set"
              loading={loading}
              hideActions={true}
              submitRef={formSubmitRef}
            />
          </View>
        </ScrollView>
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  contentContainer: {
    paddingBottom: spacing.xl,
  },
  formContainer: {
    padding: spacing.md,
  },
});


